import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { database } from '../../Firebase';
import { ref, push, onValue, remove, update } from 'firebase/database';
import { X } from 'lucide-react';

const EmployeeInfo = () => {
    const location = useLocation();
    const navigate = useNavigate();
    // eslint-disable-next-line no-unused-vars
    const { grampanchayatName, arthikVarshLabel } = location.state || {};

    const [view, setView] = useState('list'); // 'list' or 'form'
    const [searchTerm, setSearchTerm] = useState('');
    const [employees, setEmployees] = useState([]);
    
    // Modal State
    const [isPersonModalOpen, setIsPersonModalOpen] = useState(false);
    const [modalView, setModalView] = useState('list'); // 'list' or 'add'
    const [modalTarget, setModalTarget] = useState(''); // 'employee' or 'father'
    
    // Search States
    const [personSearchTerm, setPersonSearchTerm] = useState(''); // Aadhar
    const [firstNameSearch, setFirstNameSearch] = useState('');
    const [fatherNameSearch, setFatherNameSearch] = useState('');
    const [surnameSearch, setSurnameSearch] = useState('');

    const [persons, setPersons] = useState([]);

    // Person Form State
    const initialPersonState = {
        title: '',
        firstName: '',
        fatherName: '',
        surname: '',
        suffix: '',
        identityMark: '',
        mobileNo: '',
        aadharNo: '',
        villageName: 'इनाम धामणी'
    };
    const [personFormData, setPersonFormData] = useState(initialPersonState);

    // Edit State
    const [isEditing, setIsEditing] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const initialFormState = {
        employeeName: '',
        fatherName: '',
        address: '',
        joiningDate: '2025-04-01',
        department: 'पाणीपुरवठा',
        designation: 'Supervisor',
        type: 'Permanent',
        
        // Salary Info
        basicPay: '',
        payScale: '',
        incrementPeriod: '',
        incrementAmount: '',
        salaryMonth: 'जानेवारी',
        salaryYear: '2025',

        salaryHistory: [] // To store the salary table rows
    };

    const [formData, setFormData] = useState(initialFormState);

    // Fetch Employees & Persons
    useEffect(() => {
        const empRef = ref(database, 'Grampanchayat/Panipuravtha/Employees');
        const personRef = ref(database, 'Grampanchayat/Panipuravtha/Persons');

        const unsubscribeEmp = onValue(empRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const loadEmp = Object.keys(data).map((key) => ({
                    id: key,
                    ...data[key],
                }));
                setEmployees(loadEmp);
            } else {
                setEmployees([]);
            }
        });

        const unsubscribePerson = onValue(personRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const loadPersons = Object.keys(data).map((key) => ({
                    id: key,
                    ...data[key],
                }));
                setPersons(loadPersons);
            } else {
                setPersons([]);
            }
        });

        return () => {
            unsubscribeEmp();
            unsubscribePerson();
        }
    }, []);


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePersonInputChange = (e) => {
        const { name, value } = e.target;
        setPersonFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        if (!formData.employeeName) {
            alert("कृपया कर्मचारी नाव निवडा");
            return;
        }

        if (isEditing && selectedId) {
            const empRef = ref(database, `Grampanchayat/Panipuravtha/Employees/${selectedId}`);
            update(empRef, formData)
                .then(() => {
                    alert("Data Updated Successfully!");
                    resetForm();
                })
                .catch(err => alert("Error updating: " + err.message));
        } else {
            const empRef = ref(database, 'Grampanchayat/Panipuravtha/Employees');
            push(empRef, formData)
                .then(() => {
                    alert("Data Saved Successfully!");
                    resetForm();
                })
                .catch(err => alert("Error saving: " + err.message));
        }
    };

    const handleSavePerson = () => {
        if (!personFormData.firstName || !personFormData.surname) {
            alert("कृपया नाव आणि आडनाव भरा");
            return;
        }

        const personRef = ref(database, 'Grampanchayat/Panipuravtha/Persons');
        const usersRef = ref(database, 'Grampanchayat/Panipuravtha/Users');
        
        // Push to Persons
        push(personRef, personFormData)
            .then(() => {
                // Also Push to Users as requested
                push(usersRef, personFormData);
                
                alert("Person Saved Successfully!");
                setPersonFormData(initialPersonState);
                setModalView('list');
                // clear search terms
                setFirstNameSearch('');
                setFatherNameSearch('');
                setSurnameSearch('');
                setPersonSearchTerm('');
            })
            .catch(err => alert("Error saving person: " + err.message));
    };

    const handleDelete = (id) => {
        if(window.confirm("Are you sure you want to delete?")) {
             const empRef = ref(database, `Grampanchayat/Panipuravtha/Employees/${id}`);
             remove(empRef);
        }
    }

    const resetForm = () => {
        setFormData(initialFormState);
        setIsEditing(false);
        setSelectedId(null);
        setView('list');
    };

    const handleEdit = (emp) => {
        setFormData(emp);
        setSelectedId(emp.id);
        setIsEditing(true);
        setView('form');
    };

    const openPersonModal = (target) => {
        setModalTarget(target);
        setIsPersonModalOpen(true);
        setPersonSearchTerm('');
        setFirstNameSearch('');
        setFatherNameSearch('');
        setSurnameSearch('');
        setModalView('list');
    };

    const handleNavinClick = () => {
        setPersonFormData(prev => ({
            ...prev,
            firstName: firstNameSearch,
            fatherName: fatherNameSearch,
            surname: surnameSearch,
            aadharNo: personSearchTerm
        }));
        setModalView('add');
    };

    const handlePersonSelect = (person) => {
        if (modalTarget === 'employee') {
            setFormData(prev => ({
                ...prev,
                employeeName: `${person.firstName} ${person.fatherName} ${person.surname}`
            }));
        } else if (modalTarget === 'father') {
            setFormData(prev => ({
                ...prev,
                fatherName: `${person.firstName} ${person.fatherName} ${person.surname}`
            }));
        }
        setIsPersonModalOpen(false);
    };

    const addSalaryHandler = () => {
        if (!formData.basicPay || !formData.payScale) {
            alert("कृपया वेतन आणि पगार स्केल भरा");
            return;
        }

        const newSalaryEntry = {
            basicPay: formData.basicPay,
            payScale: formData.payScale,
            incrementPeriod: formData.incrementPeriod,
            incrementAmount: formData.incrementAmount,
            salaryMonth: formData.salaryMonth,
            salaryYear: formData.salaryYear
        };

        setFormData(prev => ({
            ...prev,
            salaryHistory: [...(prev.salaryHistory || []), newSalaryEntry],
            // Clear inputs
            basicPay: '',
            payScale: '',
            incrementPeriod: '',
            incrementAmount: ''
        }));
    };

    const removeSalaryHandler = (index) => {
        setFormData(prev => ({
            ...prev,
            salaryHistory: prev.salaryHistory.filter((_, i) => i !== index)
        }));
    };

    // Report State
    const [isReportOpen, setIsReportOpen] = useState(false);
    const [selectedReportEmployee, setSelectedReportEmployee] = useState(null);

    const handleReportClick = (emp) => {
        setSelectedReportEmployee(emp);
        setIsReportOpen(true);
    };

    const closeReport = () => {
        setIsReportOpen(false);
        setSelectedReportEmployee(null);
    };

    const handlePrint = () => {
        window.print();
    };

    useEffect(() => {
        if (isReportOpen && selectedReportEmployee) {
            const timer = setTimeout(() => {
                window.print();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isReportOpen, selectedReportEmployee]);

    if (isReportOpen && selectedReportEmployee) {
        return (
            <div className="min-h-screen bg-white p-8 font-poppins text-black">
                <style>{`
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        #report-container, #report-container * {
                            visibility: visible;
                        }
                        #report-container {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100%;
                            margin: 0;
                            padding: 20px;
                            background: white; 
                        }
                        .print-hidden {
                            display: none !important;
                        }
                    }
                `}</style>
                {/* Print Controls */}
                <div className="flex justify-end gap-4 mb-8 print-hidden">
                    <button 
                        onClick={handlePrint}
                        className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                    >
                        Print Report
                    </button>
                    <button 
                        onClick={closeReport}
                        className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 font-medium"
                    >
                        Back
                    </button>
                </div>

                {/* Report Content */}
                <div id="report-container" className="max-w-4xl mx-auto border border-black p-8 print:border-0 print:p-0">
                    <div className="text-center mb-6">
                        <h1 className="text-2xl font-bold mb-2">नमुना १३</h1>
                        <h2 className="text-lg font-semibold mb-2">सन 2025 - 2026 मधील कर्मचारी वर्गाचीसुची व वेतन श्रेणी नोंद वही</h2>
                        <h3 className="text-xl font-bold underline">ग्रामपंचायत इनाम धामणी तालुका मिरज जिल्हा सांगली</h3>
                    </div>

                    <div className="border border-black mt-4">
                        <div className="grid grid-cols-12 border-b border-black">
                            {/* Column 1 Header */}
                            <div className="col-span-4 p-4 border-r border-black font-bold text-center flex items-center justify-center">
                                नेमणुकीचे नाव व क्रमांक
                            </div>
                            
                            {/* Column 2 Header */}
                            <div className="col-span-6 border-r border-black">
                                <div className="p-2 border-b border-black font-bold text-center">
                                    2025-2026 पासून मंजूर झालेले वेतनमान
                                </div>
                                <div className="p-2 font-bold text-center">
                                    प्रत्येक नेमणुकीचा पगार *
                                </div>
                            </div>

                            {/* Column 3 Header */}
                            <div className="col-span-2 p-4 font-bold text-center flex items-center justify-center">
                                सरपंचाचा प्राधिकार व त्याची सही
                            </div>
                        </div>

                        {/* Data Row */}
                        <div className="grid grid-cols-12 min-h-[200px]">
                            <div className="col-span-4 p-4 border-r border-black">
                                <p className="font-semibold">1. {selectedReportEmployee.employeeName}</p>
                                <p className="text-sm text-gray-600 mt-1">({selectedReportEmployee.designation})</p>
                            </div>
                            
                            <div className="col-span-6 border-r border-black p-4">
                                {selectedReportEmployee.salaryHistory && selectedReportEmployee.salaryHistory.length > 0 ? (
                                    selectedReportEmployee.salaryHistory.map((salary, idx) => (
                                        <div key={idx} className="mb-2 pb-2 border-b border-gray-300 last:border-0">
                                            <p className="font-medium">
                                                {salary.salaryMonth}/{salary.salaryYear} पासून मंजूर झालेले वेतनमान - {salary.payScale || 'N/A'} - {salary.basicPay || 'N/A'}
                                            </p>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-gray-500 italic">No salary history recorded.</p>
                                )}
                            </div>

                            <div className="col-span-2 p-4 flex items-end justify-center">
                                {/* Signature Space */}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 text-right font-bold print:mt-16">
                        सरपंच / ग्रामसेवक
                    </div>
                </div>
            </div>
        );
    }

    const filteredEmployees = employees.filter(emp => 
        emp.employeeName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredPersons = persons.filter(p => 
        (p.firstName?.toLowerCase() || '').includes(firstNameSearch.toLowerCase()) &&
        (p.fatherName?.toLowerCase() || '').includes(fatherNameSearch.toLowerCase()) &&
        (p.surname?.toLowerCase() || '').includes(surnameSearch.toLowerCase()) &&
        (p.aadharNo || '').includes(personSearchTerm)
    );

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8 font-poppins print:hidden">
            
            {/* Header */}
             <div className="bg-blue-800 text-white rounded-t-lg flex justify-between items-center p-3 px-6 mb-0">
                 <h2 className="text-xl font-medium">कर्मचारी यादी</h2>
                 <div className="flex gap-2">
                    <button 
                        onClick={() => navigate('/panipuravtha/permanent-info', { state: location.state })}
                        className="px-4 py-1 bg-white/20 text-white rounded hover:bg-white/30 transition text-sm font-medium"
                    >
                        मागे जा
                    </button>
                     {view === 'list' && (
                         <button 
                            onClick={() => {
                                resetForm();
                                setView('form');
                            }}
                            className="px-4 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition"
                        >
                            नविन
                        </button>
                     )}
                     {view === 'form' && (
                         <button 
                            onClick={() => setView('list')}
                            className="text-white hover:text-gray-300"
                        >
                            <X size={24}/>
                        </button>
                     )}
                 </div>
             </div>

             {view === 'list' ? (
                /* LIST VIEW */
                <div className="bg-white shadow-md rounded-b-lg overflow-hidden border border-t-0 border-blue-800">
                    
                    {/* Search */}
                    <div className="p-4 flex gap-2">
                        <div className="flex-1 relative">
                            <input 
                                type="text"
                                placeholder="शोधणे"
                                className="w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                            शोधणे
                        </button>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-blue-800 text-white">
                                <tr>
                                    <th className="p-3 border-r border-blue-700">कर्मचारी नाव</th>
                                    <th className="p-3 border-r border-blue-700 text-center">हुद्दा</th>
                                    <th className="p-3 border-r border-blue-700 text-center">प्रकार</th>
                                    <th className="p-3 border-r border-blue-700 text-center">विभाग</th>
                                    <th className="p-3 text-center">पर्याय</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEmployees.length > 0 ? (
                                    filteredEmployees.map(emp => (
                                        <tr key={emp.id} className="border-b hover:bg-gray-50">
                                            <td className="p-3 border-r">{emp.employeeName}</td>
                                            <td className="p-3 border-r text-center">{emp.designation}</td>
                                            <td className="p-3 border-r text-center">{emp.type}</td>
                                            <td className="p-3 border-r text-center">{emp.department}</td>
                                            <td className="p-3 flex justify-center gap-2">
                                                <button 
                                                    onClick={() => handleEdit(emp)}
                                                    className="px-4 py-1 bg-blue-800 text-white rounded text-sm hover:bg-blue-900"
                                                >
                                                    बदल
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(emp.id)}
                                                    className="px-4 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                                                >
                                                    खोडणे
                                                </button>
                                                <button 
                                                    onClick={() => handleReportClick(emp)}
                                                    className="px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                                                >
                                                    रिपोर्ट
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="p-4 text-center text-gray-500">
                                            No records found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="p-2 bg-gradient-to-r from-blue-700 to-blue-900 border-t flex justify-center items-center gap-2 text-white">
                         <button className="p-1 px-3 bg-white/20 rounded hover:bg-white/30 text-sm">|&lt;</button>
                         <button className="p-1 px-3 bg-white/20 rounded hover:bg-white/30 text-sm">&lt;&lt;</button>
                         <span className="p-1 px-3 bg-blue-600 rounded text-sm">1</span>
                         <button className="p-1 px-3 bg-white/20 rounded hover:bg-white/30 text-sm">&gt;&gt;</button>
                         <button className="p-1 px-3 bg-white/20 rounded hover:bg-white/30 text-sm">&gt;|</button>
                         <select className="ml-2 bg-white text-black text-sm p-1 rounded">
                             <option>5</option>
                             <option>10</option>
                         </select>
                    </div>

                </div>
             ) : (
                /* FORM VIEW */
                <div className="bg-white shadow-md rounded-b-lg p-6 border border-t-0 border-blue-800">
                    <div className="space-y-6">
                        
                        {/* Employee Name */}
                        <div className="grid grid-cols-12 gap-4 items-center">
                            <label className="col-span-12 md:col-span-2 text-gray-700">कर्मचारी नाव:</label>
                            <div className="col-span-12 md:col-span-9">
                                <input 
                                    type="text" 
                                    name="employeeName"
                                    value={formData.employeeName}
                                    readOnly
                                    placeholder="कर्मचारी नाव"
                                    className="w-full p-2 border border-blue-300 rounded bg-gray-100 cursor-pointer"
                                    onClick={() => openPersonModal('employee')}
                                />
                            </div>
                            <div className="col-span-12 md:col-span-1">
                                <button 
                                    onClick={() => openPersonModal('employee')}
                                    className="w-full p-2 bg-blue-800 text-white rounded hover:bg-blue-900"
                                >
                                    ...
                                </button>
                            </div>
                        </div>

                        {/* Father Name */}
                        <div className="grid grid-cols-12 gap-4 items-center">
                            <label className="col-span-12 md:col-span-2 text-gray-700">वडिलांचे नाव :</label>
                            <div className="col-span-12 md:col-span-9">
                                <input 
                                    type="text" 
                                    name="fatherName"
                                    value={formData.fatherName}
                                    readOnly
                                    placeholder="वडिलांचे नाव"
                                    className="w-full p-2 border border-blue-300 rounded bg-gray-100 cursor-pointer"
                                    onClick={() => openPersonModal('father')}
                                />
                            </div>
                            <div className="col-span-12 md:col-span-1">
                                <button 
                                    onClick={() => openPersonModal('father')}
                                    className="w-full p-2 bg-blue-800 text-white rounded hover:bg-blue-900"
                                >
                                    ...
                                </button>
                            </div>
                        </div>

                        {/* Address */}
                         <div className="grid grid-cols-12 gap-4 items-start">
                            <label className="col-span-12 md:col-span-2 text-gray-700">पत्ता:</label>
                            <div className="col-span-12 md:col-span-10">
                                <textarea 
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    placeholder="पत्ता भरा."
                                    rows="2"
                                    className="w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* Joining Date & Department */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="grid grid-cols-12 gap-4 items-center">
                                <label className="col-span-4 text-gray-700">रुजु दिनांक:</label>
                                <div className="col-span-8">
                                    <input 
                                        type="date" 
                                        name="joiningDate"
                                        value={formData.joiningDate}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-blue-300 rounded"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 items-center">
                                <label className="col-span-4 text-gray-700">विभाग:</label>
                                <div className="col-span-8">
                                    <input 
                                        type="text" 
                                        name="department"
                                        value="पाणीपुरवठा" 
                                        readOnly
                                        className="w-full p-2 border-0 bg-transparent font-medium" 
                                    />
                                </div>
                            </div>
                        </div>

                         {/* Designation & Type */}
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="grid grid-cols-12 gap-4 items-center">
                                <label className="col-span-4 text-gray-700">हुद्दा:</label>
                                <div className="col-span-8">
                                    <select 
                                        name="designation"
                                        value={formData.designation}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-blue-300 rounded"
                                    >
                                        <option value="Supervisor">Supervisor</option>
                                        <option value="Clerk">Clerk</option>
                                        <option value="Peon">Peon</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 items-center">
                                <label className="col-span-4 text-gray-700">प्रकार:</label>
                                <div className="col-span-8">
                                    <select 
                                        name="type"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-blue-300 rounded"
                                    >
                                        <option value="Permanent">Permanent</option>
                                        <option value="Contract">Contract</option>
                                        <option value="Temporary">Temporary</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Salary Info Section */}
                        <div className="bg-blue-800 text-white p-2 px-4 rounded-t mt-8">
                            पगार माहिती
                        </div>
                        <div className="border border-blue-800 border-t-0 p-4 rounded-b space-y-4">
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="grid grid-cols-12 gap-4 items-center">
                                    <label className="col-span-4 text-gray-700 text-sm">वेतन:</label>
                                    <div className="col-span-8">
                                        <input type="text" name="basicPay" value={formData.basicPay} onChange={handleInputChange} placeholder="वेतन पगार भरा." className="w-full p-2 border border-blue-300 rounded" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-12 gap-4 items-center">
                                    <label className="col-span-4 text-gray-700 text-sm">पगाराचे स्केल:</label>
                                    <div className="col-span-8">
                                         <input type="text" name="payScale" value={formData.payScale} onChange={handleInputChange} placeholder="पगार स्केल माहिती भरा." className="w-full p-2 border border-blue-300 rounded" />
                                    </div>
                                </div>
                            </div>

                             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="grid grid-cols-12 gap-4 items-center">
                                    <label className="col-span-4 text-gray-700 text-sm">वाढीचा काळ:</label>
                                    <div className="col-span-8">
                                        <input type="text" name="incrementPeriod" value={formData.incrementPeriod} onChange={handleInputChange} placeholder="वाढीचा काळ महिन्यामध्ये भरा" className="w-full p-2 border border-blue-300 rounded" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-12 gap-4 items-center">
                                    <label className="col-span-4 text-gray-700 text-sm">वाढीची<br/>रक्कम:</label>
                                    <div className="col-span-8">
                                         <input type="text" name="incrementAmount" value={formData.incrementAmount} onChange={handleInputChange} placeholder="वाढीची रक्कम भरा." className="w-full p-2 border border-blue-300 rounded" />
                                    </div>
                                </div>
                            </div>

                             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="grid grid-cols-12 gap-4 items-center">
                                    <label className="col-span-4 text-gray-700 text-sm">महिना:</label>
                                    <div className="col-span-8">
                                        <select name="salaryMonth" value={formData.salaryMonth} onChange={handleInputChange} className="w-full p-2 border border-blue-300 rounded">
                                            <option>जानेवारी</option>
                                            <option>फेब्रुवारी</option>
                                            <option>मार्च</option>
                                            <option>एप्रिल</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-12 gap-4 items-center">
                                    <label className="col-span-4 text-gray-700 text-sm">वर्ष:</label>
                                    <div className="col-span-8">
                                         <input type="text" name="salaryYear" value={formData.salaryYear} onChange={handleInputChange} className="w-full p-2 border border-blue-300 rounded" />
                                    </div>
                                </div>
                            </div>

                             <div className="flex justify-center">
                                <button 
                                    onClick={addSalaryHandler}
                                    className="px-6 py-2 bg-emerald-500 text-white rounded hover:bg-emerald-600"
                                >
                                    यादित भरा
                                </button>
                             </div>

                             <div className="overflow-x-auto mt-4">
                                 <table className="w-full text-left border-collapse">
                                     <thead className="bg-blue-800 text-white">
                                         <tr>
                                             <th className="p-2 border-r border-blue-700 text-center">#</th>
                                             <th className="p-2 border-r border-blue-700">वेतन</th>
                                             <th className="p-2 border-r border-blue-700">पगाराचे स्केल</th>
                                             <th className="p-2 border-r border-blue-700">वाढीचा काळ</th>
                                             <th className="p-2 border-r border-blue-700">वेतन वाढीव रक्कम</th>
                                             <th className="p-2 border-r border-blue-700">महिना</th>
                                             <th className="p-2 border-r border-blue-700">वर्ष</th>
                                             <th className="p-2 text-center">पर्याय</th>
                                         </tr>
                                     </thead>
                                     <tbody>
                                         {formData.salaryHistory && formData.salaryHistory.length > 0 ? (
                                             formData.salaryHistory.map((item, index) => (
                                                 <tr key={index} className="border-b hover:bg-gray-50">
                                                     <td className="p-2 border-r text-center">{index + 1}</td>
                                                     <td className="p-2 border-r">{item.basicPay}</td>
                                                     <td className="p-2 border-r">{item.payScale}</td>
                                                     <td className="p-2 border-r">{item.incrementPeriod}</td>
                                                     <td className="p-2 border-r">{item.incrementAmount}</td>
                                                     <td className="p-2 border-r">{item.salaryMonth}</td>
                                                     <td className="p-2 border-r">{item.salaryYear}</td>
                                                     <td className="p-2 text-center">
                                                         <button 
                                                            onClick={() => removeSalaryHandler(index)}
                                                            className="text-red-600 hover:text-red-800"
                                                         >
                                                             <X size={18} />
                                                         </button>
                                                     </td>
                                                 </tr>
                                             ))
                                         ) : (
                                             <tr>
                                                 <td colSpan="8" className="p-3 text-center border text-gray-500">
                                                     No records found
                                                 </td>
                                             </tr>
                                         )}
                                     </tbody>
                                 </table>
                             </div>

                        </div>

                        <div className="flex justify-center gap-4 py-4 bg-blue-800 rounded-lg">
                            <button 
                                onClick={handleSave}
                                className="px-6 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition font-medium"
                            >
                                साठविणे
                            </button>
                            <button 
                                onClick={() => setView('list')}
                                className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition font-medium"
                            >
                                रद्द करणे
                            </button>
                        </div>

                    </div>
                </div>
             )}

             {/* Person Search Modal */}
             {isPersonModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                     <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl mx-4 overflow-hidden border border-blue-800">
                         <div className="bg-blue-800 text-white p-3 flex justify-between items-center">
                             <h3 className="text-lg font-medium">व्यक्तीची माहिती</h3>
                             <button onClick={() => setIsPersonModalOpen(false)} className="hover:text-red-300">
                                 <X size={24} />
                             </button>
                         </div>
                         <div className="p-4 bg-blue-50/20 max-h-[80vh] overflow-y-auto">
                             
                             {modalView === 'list' ? (
                                <>
                                 {/* Search Filters */}
                                 <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                     <div>
                                         <label className="block text-gray-700 text-sm mb-1">आधार क्र.</label>
                                         <input 
                                            type="text" 
                                            placeholder="आधार नं." 
                                            className="w-full p-2 border border-blue-300 rounded"
                                            value={personSearchTerm}
                                            onChange={(e) => setPersonSearchTerm(e.target.value)} 
                                        />
                                     </div>
                                     <div>
                                         <label className="block text-gray-700 text-sm mb-1">नाव</label>
                                         <input 
                                            type="text" 
                                            placeholder="पहिले नाव" 
                                            className="w-full p-2 border border-blue-300 rounded" 
                                            value={firstNameSearch}
                                            onChange={(e) => setFirstNameSearch(e.target.value)}
                                         />
                                     </div>
                                     <div>
                                         <label className="block text-gray-700 text-sm mb-1">वडिलांचे/पतीचे नाव</label>
                                         <input 
                                            type="text" 
                                            placeholder="वडिलांचे/पतीचे नाव" 
                                            className="w-full p-2 border border-blue-300 rounded" 
                                            value={fatherNameSearch}
                                            onChange={(e) => setFatherNameSearch(e.target.value)}
                                         />
                                     </div>
                                     <div>
                                         <label className="block text-gray-700 text-sm mb-1">आडनाव</label>
                                         <input 
                                            type="text" 
                                            placeholder="आडनाव" 
                                            className="w-full p-2 border border-blue-300 rounded" 
                                            value={surnameSearch}
                                            onChange={(e) => setSurnameSearch(e.target.value)}
                                         />
                                     </div>
                                 </div>

                                 <div className="flex justify-end mb-2">
                                     <button 
                                        onClick={handleNavinClick}
                                        className="px-6 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 font-medium"
                                     >
                                         नविन
                                     </button>
                                 </div>
                                
                                 {/* Table */}
                                 <div className="border border-blue-300">
                                     <table className="w-full text-left bg-white">
                                         <thead className="bg-blue-800 text-white sticky top-0">
                                             <tr>
                                                 <th className="p-2 border-r border-blue-700 w-20 text-center">व्यक्ती नं.</th>
                                                 <th className="p-2 border-r border-blue-700">नाव</th>
                                                 <th className="p-2 border-r border-blue-700">वडिल/पतीचे नाव</th>
                                                 <th className="p-2 border-r border-blue-700">आडनाव</th>
                                                 <th className="p-2">आधार नं.</th>
                                             </tr>
                                         </thead>
                                         <tbody>
                                                {filteredPersons.length > 0 ? (
                                                    filteredPersons.map(person => (
                                                        <tr 
                                                            key={person.id} 
                                                            onClick={() => handlePersonSelect(person)}
                                                            className="border-b hover:bg-blue-50 cursor-pointer"
                                                        >
                                                            <td className="p-2 border-r text-center">{person.id?.substring(1,5)}..</td>
                                                            <td className="p-2 border-r">{person.firstName}</td>
                                                            <td className="p-2 border-r">{person.fatherName}</td>
                                                            <td className="p-2 border-r">{person.surname}</td>
                                                            <td className="p-2">{person.aadharNo}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="p-3 text-center text-gray-500">
                                                            No records found
                                                        </td>
                                                    </tr>
                                                )}
                                         </tbody>
                                     </table>
                                 </div>
                                </>
                             ) : (
                                 /* ADD PERSON FORM VIEW */
                                 <div className="space-y-4">
                                     <div className="grid grid-cols-12 gap-4 items-center">
                                         <label className="col-span-3 text-gray-700">शीर्षक</label>
                                         <div className="col-span-9">
                                             <select 
                                                name="title" 
                                                value={personFormData.title} 
                                                onChange={handlePersonInputChange}
                                                className="w-full p-2 border border-blue-300 rounded"
                                             >
                                                 <option value="">-</option>
                                                 <option value="Mr">Mr</option>
                                                 <option value="Mrs">Mrs</option>
                                                 <option value="Miss">Miss</option>
                                             </select>
                                         </div>
                                     </div>

                                     <div className="grid grid-cols-12 gap-4 items-center">
                                         <label className="col-span-3 text-gray-700">पहिले नाव</label>
                                         <div className="col-span-9">
                                             <input type="text" name="firstName" value={personFormData.firstName} onChange={handlePersonInputChange} placeholder="पहिले नाव" className="w-full p-2 border border-blue-300 rounded" />
                                         </div>
                                     </div>

                                     <div className="grid grid-cols-12 gap-4 items-center">
                                         <label className="col-span-3 text-gray-700">वडिलांचे/पतीचे नाव</label>
                                         <div className="col-span-9">
                                             <input type="text" name="fatherName" value={personFormData.fatherName} onChange={handlePersonInputChange} placeholder="वडिलांचे/पतीचे नाव" className="w-full p-2 border border-blue-300 rounded" />
                                         </div>
                                     </div>

                                     <div className="grid grid-cols-12 gap-4 items-center">
                                         <label className="col-span-3 text-gray-700">आडनाव</label>
                                         <div className="col-span-9">
                                             <input type="text" name="surname" value={personFormData.surname} onChange={handlePersonInputChange} placeholder="आडनाव" className="w-full p-2 border border-blue-300 rounded" />
                                         </div>
                                     </div>

                                     <div className="grid grid-cols-12 gap-4 items-center">
                                         <label className="col-span-3 text-gray-700">सफिक्स</label>
                                         <div className="col-span-9">
                                              <select name="suffix" value={personFormData.suffix} onChange={handlePersonInputChange} className="w-full p-2 border border-blue-300 rounded">
                                                 <option value="">-</option>
                                                 <option value="Jr">Jr</option>
                                                 <option value="Sr">Sr</option>
                                             </select>
                                         </div>
                                     </div>

                                     <div className="grid grid-cols-12 gap-4 items-center">
                                         <label className="col-span-3 text-gray-700">ओळख चिन्ह</label>
                                         <div className="col-span-9">
                                             <input type="text" name="identityMark" value={personFormData.identityMark} onChange={handlePersonInputChange} placeholder="ओळख चिन्ह" className="w-full p-2 border border-blue-300 rounded" />
                                         </div>
                                     </div>

                                     <div className="grid grid-cols-12 gap-4 items-center">
                                         <label className="col-span-3 text-gray-700">भ्रमणध्वनी क्र</label>
                                         <div className="col-span-9">
                                             <input type="text" name="mobileNo" value={personFormData.mobileNo} onChange={handlePersonInputChange} placeholder="भ्रमणध्वनी क्र" className="w-full p-2 border border-blue-300 rounded" />
                                         </div>
                                     </div>

                                     <div className="grid grid-cols-12 gap-4 items-center">
                                         <label className="col-span-3 text-gray-700">आधार क्रमांक</label>
                                         <div className="col-span-9">
                                             <input type="text" name="aadharNo" value={personFormData.aadharNo} onChange={handlePersonInputChange} placeholder="आधार क्रमांक" className="w-full p-2 border border-blue-300 rounded" />
                                         </div>
                                     </div>

                                      <div className="grid grid-cols-12 gap-4 items-center">
                                         <label className="col-span-3 text-gray-700">गावाचे नाव</label>
                                         <div className="col-span-9">
                                             <select name="villageName" value={personFormData.villageName} onChange={handlePersonInputChange} className="w-full p-2 border border-blue-300 rounded">
                                                 <option value="इनाम धामणी">इनाम धामणी</option>
                                                 {/* Add more options if needed */}
                                             </select>
                                         </div>
                                     </div>

                                     <div className="flex justify-center gap-4 py-4 bg-blue-800 rounded-lg mt-4">
                                        <button 
                                            onClick={handleSavePerson}
                                            className="px-6 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition font-medium"
                                        >
                                            साठविणे
                                        </button>
                                        <button 
                                            onClick={() => setModalView('list')}
                                            className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition font-medium"
                                        >
                                            रद्द करा
                                        </button>
                                    </div>
                                 </div>
                             )}

                         </div>
                     </div>
                </div>
             )}

        </div>
    );
};

export default EmployeeInfo;
